// Note: Currently these numbers are fixed and should not be changed.
// Changing these would require changing the CSS variables and position styles plus other logic.

// Multiple indicator dots on the edge indicate more pages existing beyond that nav edge.
// A single indicator dot on the edge indicates no more pages exist beyond that nav edge.
// Indicator dots will trigger a dot shift (position change) on the nav.
// Indicator dots will only be visible if the number of pages exceed the movableNavPageThreshold.
export const maxEdgeIndicatorDots: number = 2;

// Full dots do not trigger a transition.
// A selected dot will always be represented by a full dot.
export const numberFullDots: number = 5;

// A threshold for enabling a movable nav (with dot shift/movement).
// A page count <= movableNavPageThreshold will render all nav dots as full dots.
export const movableNavPageThreshold: number = numberFullDots + maxEdgeIndicatorDots;

// Maximum number of dots on the nav.
// This corresponds to the number of positions/slots available to be occuplied by a dot on the nav.
export const maxDotLimit: number = numberFullDots + (maxEdgeIndicatorDots * 2);

export const firstFullDotPosition: number = maxEdgeIndicatorDots + 1;
export const lastFullDotPosition: number = firstFullDotPosition + numberFullDots - 1;

export type IDirection = 'RTL' | 'LTR' | '';

// CSS class names for indicator dot transitions to be performed by the CSSTransition component.
// Only dots corresponding to pages entering or exiting the nav will use these transitions.
// All other dot shifts/transitions are based on the dot (INavDot) position and size properties.
export type INavTransition = {
    enterStart: string;
    enterEnd: string;
    exit: string;
};

export type INavDot = {
    page: number;
    size: string;
    position: number;
    transition: INavTransition;
};

export type INavState = {
    dots: INavDot[];
    selectedDot: number;
    selectedPage: number;
    inNavSelection: boolean,
    static: boolean,
};

export function getNavState (prevNav: INavState, pageNumber: number, totalPages: number): INavState {
    if (pageNumber === prevNav.selectedPage) {
        return prevNav;
    }

    const inNavSelection: boolean = pageIsOnNav(prevNav.dots, pageNumber);
    const selectedPage: number = normalizePageNumber(pageNumber, totalPages);
    const navDots: INavDot[] = getNewDots(prevNav, selectedPage, totalPages, inNavSelection);

    return {
        dots: navDots,
        selectedDot: getNavDotNumber(navDots, selectedPage),
        selectedPage,
        inNavSelection,
        static: isStaticNav(totalPages),
    };
}

export function isStaticNav (totalPages: number): boolean {
    return totalPages <= movableNavPageThreshold;
}

function getNewDots (prevNav: INavState, selectedPage: number, totalPages: number, inNavSelection: boolean): INavDot[] {
    const dotShift: number = getDotShift(prevNav, totalPages, selectedPage, inNavSelection);
    const totalDots: number = getNewDotsCount(prevNav, totalPages, selectedPage, inNavSelection);
    const pagePositions: number[] = getPagePositions(prevNav, totalDots, totalPages, selectedPage, dotShift, inNavSelection);
    const dots: INavDot[] = [];

    pagePositions.forEach((pageNumber: number, index): void => {
        if (pageNumber) {
            const pagePosition: number = index + 1;
            const dotSize: string = getDotSize(pagePosition, totalPages);

            dots.push({
                page: pageNumber,
                position: pagePosition,
                size: dotSize,
                transition: getDotTransition(prevNav, totalPages, totalDots, dotSize, pagePosition, dotShift, inNavSelection),
            });
        }
    });

    return dots;
}

// positive dotShift indicates dots shifting Right, which happens when going from page 5 to 4.
function getDotShift (prevNav: INavState, totalPages: number, selectedPage: number, inNavSelection: boolean): number {
    if (totalPages <= movableNavPageThreshold || !inNavSelection) {
        return 0;
    }

    const prevDotIndex: number = getDotIndexByPage(prevNav.dots, selectedPage);
    const selectedDot: INavDot | undefined = prevNav.dots[prevDotIndex];

    if (selectedDot.position < firstFullDotPosition) {
        return firstFullDotPosition - selectedDot.position;
    }

    if (selectedDot.position > lastFullDotPosition) {
        return -(selectedDot.position - lastFullDotPosition);
    }

    return 0;
}

function getDotPageList (firstDotPage: number, firstDotPosition: number, visibleDots: number): number[] {
    const firstDotIndex: number = firstDotPosition - 1;
    const lastDotIndex: number = firstDotIndex + visibleDots - 1;

    return Array(maxDotLimit).fill(0).map((_, index: number): number => {
        if (index >= firstDotIndex && index <= lastDotIndex) {
            return firstDotPage + index - firstDotIndex;
        }

        return 0;
    });
}

function getPagePositionsOffNavSelection (selectedPage: number, totalPages: number): number[] {
    let newFirstDotPage: number = 1;
    let newFirstDotPosition: number = maxEdgeIndicatorDots + 1;

    if (totalPages <= movableNavPageThreshold) {
        return getDotPageList(newFirstDotPage, newFirstDotPosition, totalPages);
    }

    if (selectedPage <= numberFullDots) {
        return getDotPageList(newFirstDotPage, newFirstDotPosition, movableNavPageThreshold);
    }

    if (selectedPage === numberFullDots + 1) {
        newFirstDotPage = 1;
        newFirstDotPosition = maxEdgeIndicatorDots;

        return getDotPageList(newFirstDotPage, newFirstDotPosition, movableNavPageThreshold + 1);
    }

    // last page
    if (selectedPage === totalPages) {
        newFirstDotPage = selectedPage - movableNavPageThreshold + 1;
        newFirstDotPosition = 1;

        return getDotPageList(newFirstDotPage, newFirstDotPosition, movableNavPageThreshold);
    }

    // second last page
    if (selectedPage === totalPages - 1) {
        newFirstDotPosition = 1;
        newFirstDotPage = selectedPage - movableNavPageThreshold + 1;

        return getDotPageList(newFirstDotPage, newFirstDotPosition, movableNavPageThreshold + 1);
    }

    newFirstDotPage = selectedPage - movableNavPageThreshold + 1;
    newFirstDotPosition = 1;

    return getDotPageList(newFirstDotPage, newFirstDotPosition, maxDotLimit);
}

function getPagePositions (prevNav: INavState, totalDots: number, totalPages: number, selectedPage: number, dotShift: number, inNavSelection: boolean): number[] {
    // selected item was not on nav
    if (!inNavSelection) {
        return getPagePositionsOffNavSelection(selectedPage, totalPages);
    }

    const dotCountChange: number = totalDots - prevNav.dots.length;
    const prevFirstDotPage: number = prevNav.dots[0].page;
    const prevLastDotPage: number = prevNav.dots[prevNav.dots.length - 1].page;
    const prevFirstDotIndex: number = prevNav.dots[0].position - 1;
    let newFirstDotIndex: number = prevFirstDotIndex;

    // dots added so update first dot index
    if (dotCountChange > 0) {
        newFirstDotIndex = Math.max(0, prevFirstDotIndex - dotCountChange);
    }

    let newFirstDotPage: number = prevFirstDotPage;
    const offscreenPagesR: number = totalPages - prevLastDotPage;
    const navMove: IDirection = getNavMove(dotShift);

    if (navMove === 'LTR') {
        const availablePositionsL: number = prevFirstDotIndex;

        if (availablePositionsL === 0) {
            newFirstDotPage = Math.max(0, prevFirstDotPage - dotShift);
        }

        if (availablePositionsL > 0) {
            let pageChange: number = dotShift + dotCountChange;

            if (!offscreenPagesR) {
                pageChange = dotCountChange;
            }

            if (availablePositionsL > Math.abs(pageChange)) {
                pageChange = 0;
            }

            newFirstDotPage = Math.max(1, prevFirstDotPage - pageChange);
        }

        newFirstDotIndex = Math.max(0, prevFirstDotIndex + dotShift);
    }

    if (navMove === 'RTL') {
        newFirstDotPage = Math.max(1, prevFirstDotPage - dotShift);

        // and dots removed
        if (dotCountChange < 0) {
            newFirstDotIndex = Math.max(0, prevFirstDotIndex - dotCountChange);
        }
    }

    // overrides
    if (dotShift) {
        if (selectedPage === 1) {
            newFirstDotIndex = 2;
        }

        if (selectedPage === 2) {
            newFirstDotIndex = 1;
        }
    }

    return getDotPageList(newFirstDotPage, newFirstDotIndex + 1, totalDots);
}

function normalizePageNumber (selectedPage: number, totalPages: number): number {
    if (selectedPage > totalPages) {
        return totalPages;
    }

    if (selectedPage < 1) {
        return 1;
    }

    return selectedPage;
}

// Note: with the CSSTransition component the exit transitions must be predefined.
// Once the component is mounted the transition cannot be changed.
// This may prevents us from specifying an exit transition based on user interaction,
// such as when 1 dot exits vs 2 at a time.
function getDotTransition (prevNav: INavState, totalPages: number, totalDots: number, dotSize: string, pagePosition: number, dotShift: number, inNavSelection: boolean): INavTransition {
    let enterStart: string = '';
    let enterEnd: string = '';
    let exit: string = '';

    // no transitions
    if (!inNavSelection || totalPages <= movableNavPageThreshold || totalDots === movableNavPageThreshold) {
        return {
            enterStart,
            enterEnd,
            exit,
        };
    }

    // dot exit class is based on relative nav position only
    exit = 'offscreenRight';

    if (pagePosition < maxDotLimit / 2) {
        exit = 'offscreenLeft';
    }

    // exit transitions only
    if (!dotShift) {
        return {
            enterStart,
            enterEnd,
            exit,
        };
    }

    const availablePagesRight: boolean = prevNav.dots[prevNav.dots.length - 1].page < totalPages;
    const availablePagesLeft: boolean = prevNav.dots[0].page > 1;
    const navMove: IDirection = getNavMove(dotShift);

    if (navMove === 'LTR' && availablePagesRight) {
        if (pagePosition === 8 || pagePosition === 9) {
            enterStart = 'offscreenRight';
            enterEnd = `${dotSize}pos${pagePosition}`;
        }
    }

    if (navMove === 'RTL' && availablePagesLeft) {
        if (pagePosition === 1 || pagePosition === 2) {
            enterStart = 'offscreenLeft';
            enterEnd = `${dotSize}pos${pagePosition}`;
        }
    }

    return {
        enterStart,
        enterEnd,
        exit,
    };
}

function getDotSize (position: number, totalPages: number): string {
    if (totalPages <= movableNavPageThreshold) {
        return 'sizeL';
    }

    if (position >= firstFullDotPosition && position <= lastFullDotPosition) {
        return 'sizeL';
    }

    if (position === (firstFullDotPosition - 1) || position === (lastFullDotPosition + 1)) {
        return 'sizeM';
    }

    return 'sizeS';
}

function getNewDotsCount (prevNav: INavState, totalPages: number, selectedPage: number, inNavSelection: boolean): number {
    if (totalPages <= movableNavPageThreshold) {
        return totalPages;
    }

    const dotCountChange: number = getDotCountChange(prevNav, totalPages, selectedPage, inNavSelection);

    return prevNav.dots.length + dotCountChange;
}

function getDotCountChangeOffNavSelection (prevNav: INavState, totalPages: number, selectedPage: number): number {
    if (selectedPage <= numberFullDots || selectedPage === totalPages) {
        return prevNav.dots.length - movableNavPageThreshold;
    }

    if (selectedPage === numberFullDots + 1 || selectedPage === totalPages - 1) {
        return prevNav.dots.length - movableNavPageThreshold + 1;
    }

    return prevNav.dots.length - maxDotLimit;
}

function getDotCountChange (prevNav: INavState, totalPages: number, selectedPage: number, inNavSelection: boolean): number {
    // selected dot was not on nav
    if (!inNavSelection) {
        return getDotCountChangeOffNavSelection(prevNav, totalPages, selectedPage);
    }

    if (totalPages <= movableNavPageThreshold) {
        return 0;
    }

    const prevDotIndex: number = getDotIndexByPage(prevNav.dots, selectedPage);
    const selectedDot: INavDot = prevNav.dots[prevDotIndex];
    const actionableDot: boolean = selectedDot.position < firstFullDotPosition || selectedDot.position > lastFullDotPosition;

    if (!actionableDot) {
        return 0;
    }

    const firstDot: INavDot = prevNav.dots[0];
    const lastDot: INavDot = prevNav.dots[prevNav.dots.length - 1];
    const maxDots: number = Math.min(totalPages, maxDotLimit);
    const fullNav: boolean =  prevNav.dots.length === maxDots;
    const offscreenPagesRight: number = totalPages - lastDot.page;
    const offscreenPagesLeft: number = firstDot.page - 1;
    const availablePositionsLeft: number = firstDot.position;

    // selected 1st or last page
    if (selectedPage === 1 || selectedPage === totalPages) {
        return movableNavPageThreshold - prevNav.dots.length;
    }

    // selected 2nd or 2nd last page
    if (selectedPage === 2 || selectedPage === totalPages - 1) {
        return (maxDotLimit - 1) - prevNav.dots.length;
    }

    if (!fullNav && selectedDot.position < firstFullDotPosition) {
        return Math.min(firstFullDotPosition - selectedDot.position, offscreenPagesLeft);
    }

    if (!fullNav && selectedDot.position > lastFullDotPosition) {
        return Math.min(selectedDot.position - lastFullDotPosition, offscreenPagesRight, availablePositionsLeft - 1);
    }

    return 0;
}

function getDotIndexByPage (navDots: INavDot[], pageNumber: number): number {
    return navDots.findIndex((dot) => {
        return dot.page === pageNumber;
    });
}

function pageIsOnNav (navDots: INavDot[], pageNumber: number): boolean {
    const index: number = getDotIndexByPage(navDots, pageNumber);

    return index >= 0;
}

function getNavDotNumber (navDots: INavDot[], pageNumber: number): number {
    const index: number = getDotIndexByPage(navDots, pageNumber);

    return index + 1;
}

// positive dotShift indicates user navigating RTL (and consequently dots shifting right)
// negative dotShift indicates user navigating LTR (and consequently dots shifting left)
function getNavMove (dotShift: number): IDirection {
    if (dotShift > 0) {
        return 'RTL';
    }

    if (dotShift < 0) {
        return 'LTR';
    }

    return '';
}
